import React, { useState, useEffect, useCallback, memo } from 'react';
import { View, StyleSheet } from 'react-native';
import { airBrandMap } from '../../constants/BrandMaps';
import { Color, FontSize, CommonProperties, CommonStyles } from '../../styles';
import Images from '../../constants/Images';
import Icon from '../Icon';
import { formatCurrency, isBXcurrency } from '../../utils/currency.utils';
import { timeConvert24toAMPM, getTotalDuration, getLayoverTime } from '../../utils/time.utils';
import { getSegmentsCountText } from '../../utils/segmentsCount.util';
import { useDispatch, useSelector } from 'react-redux';
import { selectAirCloud } from '../../store/actions/air';
import Card from './Card';
import { cardStyle, XSmallText, CardLeftTextDetail, ShrinkView, PerUnitPrice } from './card.utils';

import BaseText from '../BaseText';
import PriceItem from './PriceItem';
import TextWithIcon from '../TextWithIcon';
import RowView from '../RowView';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../../i18n';
import moment from 'moment';
import {
  selectAirlinesCloud,
  selectAirportsCloud,
  selectAircraftsCloud,
} from '../../store/selectors/air.selector';
import { GDS, GdsSource } from '../../constants/Enums';

const getBrandDetailsByName = (name) => {
  const lowerCaseName = name ? name.toLowerCase() : '';
  return airBrandMap[lowerCaseName] || {};
};

function checkBrandIsNDC(brand) {
  const isNDCSource = brand.gdsKey == GdsSource.SabreNDC || brand.gdsKey == GdsSource.NDC;
  return isNDCSource ? GDS.NDC : '';
}

const getBrandDetails = (brand) => {
  let name = '';

  if (brand) {
    name = brand.brand;
  }

  return getBrandDetailsByName(name);
};

const customBrandName = (name) => {
  const lowerCaseName = name ? name.toLowerCase() : '';
  return airBrandMap[lowerCaseName]?.key || name.replace(' ', '');
};

const getSegmentsText = (list = []) => {
  if (Array.isArray(list)) {
    return list
      .reduce((acum, curr) => {
        acum.push(curr.departure);
        acum.push(curr.arrival);
        return acum;
      }, [])
      .filter((value, index, array) => value !== array[index + 1])
      .join(' - ');
  }

  return ' ';
};

const getDifferenceDays = (segments) => {
  if (!segments && !Array.isArray(segments) && segments.length > 0) {
    return '';
  }

  const startDate = segments[0].departureDate;
  const endDate = segments[segments.length - 1].arrivalDate;
  const days = moment(endDate).diff(moment(startDate), 'days');
  return days > 0 ? '+' + days : null;
};

const transformAirDataToCardCloud = (flight = {}, legIndex, currency, currencyCulture) => {
  const leg = flight?.legs[0].flights[legIndex] || {};
  const policyReasonCode =
    flight.fares && flight.fares[flight.selectedBrand]?.reasonCode?.toLowerCase();
  const segmentLength = flight.legs[0]?.flights.length - 1;
  const arrivalTime =
    segmentLength > 0
      ? timeConvert24toAMPM(flight?.legs[0].flights[segmentLength].arrivalTime)
      : timeConvert24toAMPM(flight?.legs[0].flights[0].arrivalTime);
  return {
    title: timeConvert24toAMPM(flight?.legs[0].flights[0].departureTime) + ' - ' + arrivalTime,
    titleExtra: getDifferenceDays(flight?.legs[0].flights),
    subtitle: getSegmentsText(flight?.legs[0].flights),
    value: formatCurrency(flight?.fares[0]?.total, currency, currencyCulture),
    textDownValue: getTotalDuration(flight?.legs[0]?.totalDuration),
    icon: flight.legs[0]?.isMultipleAirline
      ? Images.air['36x36']['MultipleAirlinesPNG']
      : Images.air['36x36'][leg?.marketting || flight?.legs[0].flights[0].marketting],
    textDownIcon: getSegmentsCountText(flight?.legs[0].flights.length - 1),
    policy: policyReasonCode,
    expandInsteadOfPress:
      flight?.fares?.[flight.selectedBrand] && flight.fares[flight.selectedBrand]?.isSuppressed,
  };
};

const AirDetailsItem = ({ segment, leg, flight }) => {
  const { tKey } = useComponentTKey('FlightScreen');
  const { t } = useTranslation(i18nNS.air);
  const airlines = useSelector(selectAirlinesCloud);
  const airports = useSelector(selectAirportsCloud);
  const aircrafts = useSelector(selectAircraftsCloud);
  return (
    <View style={CommonStyles.mb3}>
      <RowView>
        <CardLeftTextDetail />
        <ShrinkView>
          <XSmallText>
            {airlines?.find((airline) => airline.code === segment?.marketting)?.name +
              ' #' +
              segment?.markettingnumber}
          </XSmallText>
        </ShrinkView>
      </RowView>
      <RowView>
        <CardLeftTextDetail>D:</CardLeftTextDetail>
        <ShrinkView>
          <View style={cardStyle.textRow}>
            <XSmallText>{timeConvert24toAMPM(segment?.departureTime)}</XSmallText>
            <XSmallText style={[CommonStyles.ml3, cardStyle.grayColor]}>
              {airports?.find((airport) => airport.code === segment?.departure)?.CityName}
            </XSmallText>
          </View>
          <XSmallText style={cardStyle.grayColor}>{getTotalDuration(segment?.duration)}</XSmallText>
        </ShrinkView>
      </RowView>
      <RowView>
        <CardLeftTextDetail>A:</CardLeftTextDetail>
        <ShrinkView>
          <View style={cardStyle.textRow}>
            <XSmallText>{timeConvert24toAMPM(segment?.arrivalTime)}</XSmallText>
            <XSmallText style={[CommonStyles.ml3, cardStyle.grayColor]}>
              {airports?.find((airport) => airport.code === segment?.arrival)?.CityName}
            </XSmallText>
          </View>
          <XSmallText>
            {aircrafts?.find((aircraft) => +aircraft.code === +segment?.craftCode)?.name}
          </XSmallText>
          <XSmallText>
            {t('air:FlightScreen.' + customBrandName(flight.fares[0]?.brand))}
          </XSmallText>
          <XSmallText>
            {airlines?.find((airline) => airline.code === segment?.marketting)?.name}
          </XSmallText>
          <View style={[cardStyle.textRow, CommonStyles.mt2]}>
            {flight.IsCorporate && <BaseText style={styles.mark}>Corporate</BaseText>}
            <Icon
              style={[CommonStyles.py1, CommonStyles.ml2]}
              name="wifi"
              size={FontSize.xSmall}
              color={Color.primary}
            />
            <Icon
              style={[CommonStyles.py1, CommonStyles.ml2]}
              name="power"
              size={FontSize.xSmall}
              color={Color.primary}
            />
          </View>
        </ShrinkView>
      </RowView>
      {segment?.layover !== 0 ? (
        <View style={styles.layover}>
          <TextWithIcon iconName="time">
            {getLayoverTime(
              segment.layover,
              airports?.find((airport) => airport.code === segment?.arrival)?.name
            )}
          </TextWithIcon>
        </View>
      ) : (
        <></>
      )}
    </View>
  );
};

const AirDetails = ({
  flight = {},
  legIndex,
  showBrands,
  showPerUnitPrice,
  currency,
  currencyCulture,
  legNumber,
  setFlightLoading,
}) => {
  const { tKey } = useComponentTKey('AirCard');
  const { t } = useTranslation(i18nNS.resultCards);
  const dispatch = useDispatch();
  const leg = flight?.legs[legIndex];
  const brands = flight.fares;
  const onBrandPressHandler = useCallback(
    async (brand, index) => {
      await dispatch(selectAirCloud(flight, brand, legNumber, setFlightLoading, index));
    },
    [dispatch, flight, legNumber, setFlightLoading]
  );
  function getDisplayNamesFromBrands(brands) {
    return Object.values(brands.reduce((acc, cur) => Object.assign(acc, { [cur.brand]: cur }), {}));
  }
  const uniqueBrands = getDisplayNamesFromBrands(brands);
  return (
    <View style={cardStyle.composedRow}>
      <ShrinkView style={CommonStyles.flex1}>
        {leg?.flights &&
          leg?.flights.map((segment, index) => (
            <AirDetailsItem key={index} segment={segment} leg={leg} flight={flight} />
          ))}
        {leg?.flights.length > 1 && (
          <View style={styles.layover}>
            <XSmallText>
              {t(tKey('Duration'))} {getTotalDuration(leg?.totalDuration)}
            </XSmallText>
          </View>
        )}
      </ShrinkView>
      {showBrands && (
        <View style={CommonStyles.ml2}>
          {showPerUnitPrice ? (
            <>
              <PerUnitPrice
                label={t(tKey('PerPerson'))}
                value={formatCurrency(
                  isBXcurrency(currencyCulture) ? flight.TotalPoints : [],
                  isBXcurrency(currencyCulture) ? '' : currency,
                  currencyCulture
                )}
              />
            </>
          ) : (
            uniqueBrands?.map((brand, index) => (
              <>
                <PriceItem
                  key={index}
                  price={formatCurrency(
                    isBXcurrency(currencyCulture) ? brand.TotalPoints : brand.total,
                    isBXcurrency(currencyCulture) ? '' : currency,
                    currencyCulture
                  )}
                  labelUp={t('air:FlightScreen.' + customBrandName(brand.brand))}
                  isPrimary={flight.selectedBrand ? flight.selectedBrand === index : index === 0}
                  iconName={getBrandDetails(brand).icon}
                  disabled={brand?.isSuppressed}
                  onPress={() => onBrandPressHandler(brand, index)}
                  policyReasonCode={brand.reasonCode?.toLowerCase()}
                  secondLabel={checkBrandIsNDC(brand)}
                />
              </>
            ))
          )}
        </View>
      )}
    </View>
  );
};

const AirCardCloud = ({
  flight = {},
  legIndex = 0,
  showBrands,
  showPerUnitPrice,
  currency,
  currencyCulture,
  ...rest
}) => {
  const [data, setData] = useState(null);
  const [defaultBrand, setDefaultBrand] = useState(null);
  const { tKey } = useComponentTKey('FlightScreen');
  const { t } = useTranslation(i18nNS.air);
  useEffect(() => {
    if (flight && Object.keys(flight)?.length > 0) {
      const leg = flight?.fares?.[legIndex];
      if (leg && Object.keys(leg)?.length > 0) {
        setData(transformAirDataToCardCloud(flight, legIndex, currency, currencyCulture));
      }

      let brandName = '';
      if (flight.selectedBrand) {
        brandName = flight.fares[flight.selectedBrand].brand;
      } else if (flight.fares) {
        brandName = flight.fares[0]?.brand;
      } else if (leg?.AirSegments?.[0]?.AirCabinClass) {
        brandName = leg.AirSegments[0].AirCabinClass;
      }
      if (leg?.marketting?.toLowerCase() === 'wn') {
        brandName = brandName.split(' ')[0].toLowerCase();
        if (brandName === 'business') {
          brandName = 'first_wn';
        } else if (brandName === 'select') {
          brandName = 'select_wn';
        } else if (brandName === 'main') {
          brandName = 'wga_wn';
        }
      }

      if (brandName) {
        setDefaultBrand(getBrandDetailsByName(brandName.split(' ')[0]));
      }
    }
  }, [legIndex, flight]);

  return data ? (
    <Card
      title={data.title}
      titleExtra={data.titleExtra}
      subtitle={data.subtitle}
      value={data.value}
      textDownValue={data.textDownValue}
      icon={data.icon}
      textUpIcon={data.textUpIcon}
      textDownIcon={data.textDownIcon}
      policy={data.policy}
      uppercaseSubtitle={true}
      alignCenterContent={true}
      expandInsteadOfPress={data.expandInsteadOfPress}
      isAirCard={true}
      detail={
        <AirDetails
          legNumber={rest.legNumber}
          setFlightLoading={rest.setFlightLoading}
          flight={flight}
          legIndex={legIndex}
          showBrands={showBrands}
          showPerUnitPrice={showPerUnitPrice}
          currency={currency}
          currencyCulture={currencyCulture}
        />
      }
      {...rest}
    >
      <RowView>
        {defaultBrand && (
          <View style={CommonStyles.centerItems}>
            <Icon style={styles.iconBrand} name={defaultBrand.icon} />
            <XSmallText style={cardStyle.grayColor}>{t(tKey(defaultBrand.key))}</XSmallText>
          </View>
        )}

        {flight.IsCorporate && <Icon style={styles.corporate} name="corporate" />}
      </RowView>
    </Card>
  ) : (
    <></>
  );
};

const styles = StyleSheet.create({
  iconBrand: {
    fontSize: FontSize.h2,
    color: Color.primary,
    ...CommonProperties.pb2,
  },
  corporate: {
    color: Color.corporate,
    ...CommonProperties.small,
  },
  mark: {
    backgroundColor: Color.corporate,
    color: Color.accent,
    borderRadius: FontSize.xSmall,
    ...CommonProperties.py1,
    ...CommonProperties.px3,
    ...CommonProperties.xxSmall,
    ...CommonProperties.centerAlignment,
  },
  layover: {
    backgroundColor: 'rgb(245,245,245)',
    alignItems: 'center',
    ...CommonProperties.borderRadius,
    ...CommonProperties.py2,
    ...CommonProperties.mt2,
  },
});

export default memo(AirCardCloud);
